import type { LucideIcon } from 'lucide-react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../../lib/cn'

/**
 * Admin layout primitives, same theme as Login / Register / Judge:
 * light-blue page, white rounded panels, navy text, orange actions.
 * No old theme tokens (ink-*, edge, canvas, teal, ok, warn, bad) are used.
 */

export const NAVY = '#0B1E3F'

/** Fixed header card over exactly one scrolling body. */
export function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <>
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-2xl border border-white bg-white/95 px-4 py-3 shadow-lg shadow-blue-900/5 sm:px-5">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-extrabold tracking-tight text-[#0B1E3F]">{title}</h2>
          {description && <p className="mt-0.5 text-xs text-slate-500 sm:text-[13px]">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </header>

      {/*
        The only scrolling region, so the menu and header never move.

        Horizontal padding is load-bearing, not decoration: this box reserves
        ~10px of its width for the vertical scrollbar, so content stretched to
        100% sat flush against it and anything painted *outside* the content
        box — a `ring`, a focus halo — was clipped by the edge. `scrollbar-
        gutter: stable` also stops the whole column shifting sideways when a
        short section does not need a scrollbar.
      */}
      <div
        className="min-h-0 flex-1 overflow-y-auto px-2 py-3 sm:px-3 sm:py-4"
        style={{ scrollbarGutter: 'stable' }}
      >
        {children}
      </div>
    </>
  )
}

/** White rounded panel. */
export function Panel({
  title,
  description,
  actions,
  className,
  bodyClassName,
  children,
}: {
  title?: string
  description?: string
  actions?: ReactNode
  className?: string
  bodyClassName?: string
  children: ReactNode
}) {
  return (
    <section
      className={cn(
        'overflow-hidden rounded-2xl border border-white bg-white/95 shadow-lg shadow-blue-900/5',
        className,
      )}
    >
      {title && (
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-bold text-[#0B1E3F]">{title}</h3>
            {description && <p className="mt-0.5 truncate text-xs text-slate-500">{description}</p>}
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </div>
      )}
      <div className={cn('p-4 sm:p-5', bodyClassName)}>{children}</div>
    </section>
  )
}

const TILE = {
  blue: 'bg-blue-100 text-blue-600',
  orange: 'bg-orange-100 text-orange-600',
  green: 'bg-emerald-100 text-emerald-600',
  violet: 'bg-violet-100 text-violet-600',
  amber: 'bg-amber-100 text-amber-600',
} as const

/** One headline figure. */
export function Stat({
  label,
  value,
  sub,
  icon: Icon,
  color = 'blue',
  attention,
}: {
  label: string
  value: string | number
  sub?: string
  icon?: LucideIcon
  color?: keyof typeof TILE
  attention?: boolean
}) {
  return (
    <div className="flex items-center gap-3.5 rounded-2xl border border-white bg-white/95 p-4 shadow-lg shadow-blue-900/5">
      {Icon && (
        <span className={cn('grid size-11 shrink-0 place-items-center rounded-xl', TILE[color])}>
          <Icon className="size-5" strokeWidth={2.2} />
        </span>
      )}
      <div className="min-w-0">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</p>
        <p className="text-2xl font-black leading-tight tabular-nums text-[#0B1E3F]">{value}</p>
        {sub && (
          <p className={cn('text-xs leading-snug', attention ? 'font-semibold text-amber-600' : 'text-slate-500')}>
            {sub}
          </p>
        )}
      </div>
    </div>
  )
}

/** Status chip. */
export function Chip({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'positive' | 'attention' | 'critical' | 'info'
  children: ReactNode
}) {
  const tones = {
    neutral: 'bg-slate-100 text-slate-600',
    positive: 'bg-emerald-100 text-emerald-700',
    attention: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700',
    info: 'bg-blue-100 text-blue-700',
  } as const
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold', tones[tone])}>
      {children}
    </span>
  )
}

/*
 * The old `Table` primitive lived here. It set `min-w-[640px]` and relied on
 * sideways scrolling, which on a 390px phone meant scrolling to find out
 * whether a student had their certificate. `DataTable` below replaces it.
 */

// ── Responsive data list ──────────────────────────────────────

/**
 * How a column behaves on a phone, where there is no room for a row.
 *
 *   lead     the badge or avatar at the left of the card
 *   title    the headline — name, username, booth
 *   meta     a labelled line in the card body (the default)
 *   actions  buttons, pinned to the foot of the card
 *   hide     desktop only; drop it rather than crowd the card
 */
export type MobileSlot = 'lead' | 'title' | 'meta' | 'actions' | 'hide'

export interface Column<Row> {
  key: string
  /** Column heading, and the label beside the value on a phone. */
  label: string
  render: (row: Row) => ReactNode
  align?: 'right'
  mobile?: MobileSlot
  /** Widen or constrain the desktop column. */
  className?: string
}

/**
 * One row of data, rendered two ways from a single declaration.
 *
 * At `md` and above this is an ordinary table. Below it, each row becomes a
 * stacked card — because a six-column table on a 390px phone means sideways
 * scrolling to answer "did this student get their certificate?", and a column
 * you have to scroll to is a column nobody reads.
 *
 * Writing the cells twice was the obvious alternative and the wrong one: the
 * two copies drift. Here each cell is declared once and the `mobile` slot
 * says where it lands on a phone.
 */
export function DataTable<Row>({
  columns,
  rows,
  getKey,
  empty,
  loading,
}: {
  columns: Column<Row>[]
  rows: Row[]
  getKey: (row: Row) => string | number
  empty?: ReactNode
  loading?: boolean
}) {
  const lead = columns.find((c) => c.mobile === 'lead')
  const title = columns.find((c) => c.mobile === 'title')
  const actions = columns.filter((c) => c.mobile === 'actions')
  const meta = columns.filter(
    (c) => !c.mobile || c.mobile === 'meta',
  )

  if (rows.length === 0) {
    return (
      <p className="px-4 py-14 text-center text-sm text-slate-400">
        {loading ? 'Loading…' : (empty ?? 'Nothing to show yet.')}
      </p>
    )
  }

  return (
    <>
      {/* ── Phones: one card per row ── */}
      <ul className="divide-y divide-slate-100 md:hidden">
        {rows.map((row) => {
          const rowActions = actions.map((c) => c.render(row)).filter(Boolean)

          return (
            <li key={getKey(row)} className="p-4">
              <div className="flex items-start gap-3">
                {lead && <div className="shrink-0">{lead.render(row)}</div>}
                {title && <div className="min-w-0 flex-1">{title.render(row)}</div>}
              </div>

              {meta.length > 0 && (
                <dl className="mt-3 space-y-1.5">
                  {meta.map((c) => (
                    <div key={c.key} className="flex items-baseline justify-between gap-3">
                      <dt className="shrink-0 text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400">
                        {c.label}
                      </dt>
                      <dd className="min-w-0 text-right text-sm text-slate-600">{c.render(row)}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {rowActions.length > 0 && (
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  {rowActions.map((node, i) => (
                    <div key={actions[i]?.key ?? i}>{node}</div>
                  ))}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {/* ── Tablet and up: the real table ── */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/80">
              {/* `hide` only drops a column from the phone card; the table
                  still shows every one. */}
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    'px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400',
                    c.align === 'right' && 'text-right',
                    c.className,
                  )}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={getKey(row)} className="transition-colors hover:bg-slate-50/70">
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn('px-4 py-3 align-top', c.align === 'right' && 'text-right', c.className)}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/**
 * Horizontal scroller for something genuinely two-dimensional — the judge ×
 * booth matrix, which cannot become a stack of cards without ceasing to be a
 * matrix. Shows a hint on touch screens so the scroll is discoverable.
 */
export function ScrollArea({
  hint,
  children,
}: {
  hint?: string
  children: ReactNode
}) {
  return (
    <div>
      {hint && (
        <p className="mb-2 flex items-center gap-1.5 text-xs text-slate-400 lg:hidden">
          <span aria-hidden>↔</span>
          {hint}
        </p>
      )}
      <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">{children}</div>
    </div>
  )
}

/** Inline notice. Also replaces the old `Note` in the admin files. */
export function Callout({
  tone,
  icon: Icon,
  className,
  children,
}: {
  tone?: 'neutral' | 'attention' | 'critical' | 'error' | 'green' | 'positive'
  icon?: LucideIcon
  className?: string
  children: ReactNode
}) {
  const t =
    tone === 'error' || tone === 'critical'
      ? 'border-red-200 bg-red-50 text-red-700'
      : tone === 'green' || tone === 'positive'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
        : tone === 'attention'
          ? 'border-amber-200 bg-amber-50 text-amber-800'
          : 'border-blue-100 bg-blue-50/80 text-slate-600'
  return (
    <div className={cn('flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm', t, className)}>
      {Icon && <Icon className="mt-0.5 size-4 shrink-0" strokeWidth={2.2} />}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}
export const Note = Callout

/** Admin button. tone: orange (default) | navy | outline | danger. */
export function AButton({
  tone = 'orange',
  size = 'md',
  block,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: 'orange' | 'navy' | 'outline' | 'danger'
  size?: 'sm' | 'md'
  block?: boolean
}) {
  const tones = {
    orange: 'bg-orange-500 text-white shadow-sm hover:bg-orange-600',
    navy: 'bg-[#1B2A6B] text-white shadow-sm hover:bg-[#141F55]',
    outline: 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
    danger: 'bg-red-600 text-white hover:bg-red-700',
  } as const
  return (
    <button
      type="button"
      {...props}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-xl font-semibold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40 disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-9 px-3 text-xs' : 'h-11 px-5 text-sm',
        block && 'w-full',
        tones[tone],
        className,
      )}
    />
  )
}

/** Search box used in section headers. */
export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-10 w-full min-w-[200px] rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:w-64"
    />
  )
}
